import { Input, InputGroup, InputGroupProps } from "@chakra-ui/input"
import { Button, ButtonProps, InputRightAddon } from "@chakra-ui/react"
import React from "react"
import PropTypes from "prop-types"

interface FilePickerProps {
    onFileChange: (fileList: Array<File>) => void
    placeholder: string
    clearButtonLabel?: string
    hideClearButton?: boolean
    multipleFiles?: boolean
    accept?: string | undefined | any
    inputProps?: InputGroupProps
    inputGroupProps?: InputGroupProps
    buttonProps?: ButtonProps | any | undefined
}

interface FilePickerState {
    files: Array<File>
    fileName: string
}

class FilePicker extends React.Component<FilePickerProps, FilePickerState> {
    static defaultProps = {
        clearButtonLabel: "Clear",
        multipleFiles: false,
        accept: undefined as string,
        hideClearButton: false,
        inputProps: undefined as InputGroupProps,
        inputGroupProps: undefined as InputGroupProps,
        buttonProps: undefined as ButtonProps
    }

    private inputRef = React.createRef<HTMLInputElement>()

    constructor(props: FilePickerProps) {
        super(props)
        this.state = {
            files: [],
            fileName: ""
        }
    }

    public reset = (): void => this.handleOnClearClick()

    private commitFiles(files: Array<File>) {
        this.setState({ files, fileName: files.map(f => f.name).join(" & ") })
        this.props.onFileChange(files)
    }

    private handleOnFileChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        // ev.target.files is a live FileList owned by the input: it empties when
        // the input value is cleared, so copy it before touching the input.
        this.commitFiles(ev.target.files ? Array.from(ev.target.files) : [])
    }


    private handleOnClearClick = () => {
        this.commitFiles([])
        this.clearInnerInput()
    }

    private clearInnerInput() {
        if (this.inputRef?.current) {
            this.inputRef.current.value = ""
        }
    }

    private handleOnInputClick = () => {
        if (this.inputRef?.current) {
            this.inputRef.current.value = ""
            this.inputRef.current.click()
        }
    }

    render = (): JSX.Element => {
        const {
            placeholder,
            clearButtonLabel,
            hideClearButton,
            multipleFiles,
            accept,
            inputProps,
            inputGroupProps
        } = this.props

        return (
            <InputGroup  {...inputGroupProps}>
                <input
                    type="file"
                    ref={this.inputRef}
                    accept={accept}
                    style={{ display: "none" }}
                    multiple={multipleFiles}
                    onChange={this.handleOnFileChange}
                    data-testid={inputProps?._placeholder ?? placeholder}
                />
                <Input
                    placeholder={placeholder}
                    {
                    ...{
                        ...inputProps,
                        readOnly: true,
                        isReadOnly: true,
                        value: this.state.fileName,
                        onClick: this.handleOnInputClick,
                    }
                    }
                />
                {
                    !hideClearButton &&
                    <ClearButton
                        clearButtonLabel={clearButtonLabel ?? "Clear"}
                        onButtonClick={this.handleOnClearClick} />
                }
            </InputGroup>
        )
    }
}

type ClearButtonProps = Pick<FilePickerProps, "clearButtonLabel" | "buttonProps"> & {
    onButtonClick: () => void
}

const ClearButton: React.FC<ClearButtonProps> = ({
    clearButtonLabel,
    onButtonClick,
    buttonProps
}) => (
    <InputRightAddon>
        <Button {...buttonProps} onClick={onButtonClick}>{clearButtonLabel ?? "Clear"}</Button>
    </InputRightAddon>
)

ClearButton.propTypes = {
    clearButtonLabel: PropTypes.string,
    onButtonClick: PropTypes.func.isRequired,
    buttonProps: PropTypes.object,
}


export default FilePicker
